import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { UserPlus, Shield, Eye, Crown, Trash2, Pencil, X, RefreshCw } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";

interface OperatorUser {
  user_id: string;
  email: string;
  display_name: string;
  roles: string[];
  created_at: string;
}

const roleLabels: Record<string, { label: string; icon: typeof Shield; color: string }> = {
  admin: { label: "Admin", icon: Crown, color: "text-accent" },
  operator: { label: "Operator", icon: Shield, color: "text-primary" },
  operator_readonly: { label: "Nur Lesen", icon: Eye, color: "text-muted-foreground" },
};

const inputClass =
  "w-full h-10 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors";

function generatePassword(length = 16): string {
  const upper = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
  const lower = "abcdefghijklmnopqrstuvwxyz";
  const digits = "0123456789";
  const symbols = "!@#$%&*+-_=";
  const all = upper + lower + digits + symbols;
  const required = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    symbols[Math.floor(Math.random() * symbols.length)],
  ];
  const rest = Array.from({ length: length - 4 }, () => all[Math.floor(Math.random() * all.length)]);
  return [...required, ...rest].sort(() => Math.random() - 0.5).join("");
}

function PasswordField({ value, onChange, placeholder, label }: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  label: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="space-y-2">
      <label className="text-micro block">{label}</label>
      <div className="flex gap-2">
        <input
          type={visible ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className={cn(inputClass, "flex-1")}
        />
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          className="h-10 px-3 border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors text-xs"
          title={visible ? "Verbergen" : "Anzeigen"}
        >
          <Eye className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => { onChange(generatePassword()); setVisible(true); }}
          className="h-10 px-3 border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-colors text-xs gap-1.5 inline-flex items-center"
          title="Sicheres Passwort generieren"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          Generieren
        </button>
      </div>
    </div>
  );
}

function RoleSelector({ selected, onChange }: { selected: string[]; onChange: (roles: string[]) => void }) {
  const toggle = (role: string) => {
    onChange(
      selected.includes(role) ? selected.filter((r) => r !== role) : [...selected, role]
    );
  };
  return (
    <div className="flex gap-3">
      {Object.entries(roleLabels).map(([key, { label, icon: Icon, color }]) => {
        const active = selected.includes(key);
        return (
          <button
            key={key}
            type="button"
            onClick={() => toggle(key)}
            className={cn(
              "flex items-center gap-2 px-4 py-2 border text-sm transition-all",
              active
                ? "border-accent bg-accent/5 text-foreground"
                : "border-border text-muted-foreground hover:border-muted-foreground"
            )}
          >
            <Icon className={cn("w-3.5 h-3.5", active && color)} />
            {label}
          </button>
        );
      })}
    </div>
  );
}

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState<OperatorUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingUser, setEditingUser] = useState<OperatorUser | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Create form
  const [newEmail, setNewEmail] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newDisplayName, setNewDisplayName] = useState("");
  const [newRoles, setNewRoles] = useState<string[]>(["operator"]);
  const [creating, setCreating] = useState(false);

  // Edit form
  const [editDisplayName, setEditDisplayName] = useState("");
  const [editRoles, setEditRoles] = useState<string[]>([]);
  const [editPassword, setEditPassword] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase.functions.invoke("create-admin-user", {
      body: { action: "list" },
    });
    if (!error && data?.users) {
      setUsers(data.users);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleCreate = async () => {
    if (!newEmail || !newPassword) {
      toast.error("E-Mail und Passwort sind erforderlich");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }
    if (newRoles.length === 0) {
      toast.error("Mindestens eine Rolle auswählen");
      return;
    }

    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-admin-user", {
        body: {
          action: "create",
          email: newEmail,
          password: newPassword,
          display_name: newDisplayName || undefined,
          roles: newRoles,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Benutzer ${newEmail} erstellt`);
      setNewEmail("");
      setNewPassword("");
      setNewDisplayName("");
      setNewRoles(["operator"]);
      setShowCreate(false);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || "Fehler beim Erstellen");
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (u: OperatorUser) => {
    setEditingUser(u);
    setEditDisplayName(u.display_name);
    setEditRoles([...u.roles]);
    setEditPassword("");
  };

  const handleUpdate = async () => {
    if (!editingUser) return;
    if (editRoles.length === 0) {
      toast.error("Mindestens eine Rolle auswählen");
      return;
    }
    if (editPassword && editPassword.length < 6) {
      toast.error("Passwort muss mindestens 6 Zeichen haben");
      return;
    }

    setSaving(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-admin-user", {
        body: {
          action: "update",
          user_id: editingUser.user_id,
          display_name: editDisplayName,
          roles: editRoles,
          new_password: editPassword || undefined,
        },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("Benutzer aktualisiert");
      setEditingUser(null);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || "Fehler beim Aktualisieren");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (userId: string) => {
    setDeletingId(userId);
    try {
      const { data, error } = await supabase.functions.invoke("create-admin-user", {
        body: { action: "delete", user_id: userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success("Benutzer gelöscht");
      setConfirmDeleteId(null);
      fetchUsers();
    } catch (err: any) {
      toast.error(err.message || "Fehler beim Löschen");
    } finally {
      setDeletingId(null);
    }
  };

  const isSelf = (userId: string) => currentUser?.id === userId;

  return (
    <div className="space-y-6">
      <div className="p-6 border border-border bg-card space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-subheadline mb-1">Operator-Benutzer</h3>
            <p className="text-caption">
              Verwalten Sie die Benutzer mit Zugang zum Operator-Panel
            </p>
          </div>
          <Button
            variant="commit"
            onClick={() => { setShowCreate(!showCreate); setEditingUser(null); }}
            className="gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Neuer Benutzer
          </Button>
        </div>

        {/* Create form */}
        {showCreate && (
          <div className="p-5 border border-accent/20 bg-accent/5 space-y-4">
            <h4 className="text-body font-medium">Neuen Operator-Benutzer anlegen</h4>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-micro block">E-Mail *</label>
                <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="operator@craftfindus.de" className={inputClass} />
              </div>
              <div className="space-y-2">
                <label className="text-micro block">Anzeigename</label>
                <input type="text" value={newDisplayName} onChange={(e) => setNewDisplayName(e.target.value)} placeholder="z.B. Max Müller" className={inputClass} />
              </div>
            </div>
            <PasswordField value={newPassword} onChange={setNewPassword} placeholder="Mind. 6 Zeichen" label="Passwort *" />
            <div className="space-y-2">
              <label className="text-micro block">Rollen</label>
              <RoleSelector selected={newRoles} onChange={setNewRoles} />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="commit" onClick={handleCreate} disabled={creating}>
                {creating ? "Wird erstellt..." : "Benutzer erstellen"}
              </Button>
              <Button variant="ghost" onClick={() => setShowCreate(false)}>Abbrechen</Button>
            </div>
          </div>
        )}

        {/* Edit form */}
        {editingUser && (
          <div className="p-5 border border-primary/20 bg-primary/5 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-body font-medium">
                Benutzer bearbeiten: {editingUser.email || editingUser.user_id.slice(0, 8) + "…"}
              </h4>
              <button onClick={() => setEditingUser(null)} className="text-muted-foreground hover:text-foreground">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-micro block">Anzeigename</label>
                <input type="text" value={editDisplayName} onChange={(e) => setEditDisplayName(e.target.value)} placeholder="Anzeigename" className={inputClass} />
              </div>
              <PasswordField value={editPassword} onChange={setEditPassword} placeholder="Leer = unverändert" label="Neues Passwort (optional)" />
            </div>
            <div className="space-y-2">
              <label className="text-micro block">Rollen</label>
              <RoleSelector selected={editRoles} onChange={setEditRoles} />
              {isSelf(editingUser.user_id) && (
                <p className="text-xs text-muted-foreground">Eigene Admin-Rolle kann nicht entfernt werden.</p>
              )}
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="commit" onClick={handleUpdate} disabled={saving}>
                {saving ? "Wird gespeichert..." : "Änderungen speichern"}
              </Button>
              <Button variant="ghost" onClick={() => setEditingUser(null)}>Abbrechen</Button>
            </div>
          </div>
        )}

        {/* Delete confirmation */}
        {confirmDeleteId && (
          <div className="p-5 border border-destructive/30 bg-destructive/5 space-y-3">
            <p className="text-body font-medium">
              Benutzer wirklich löschen?
            </p>
            <p className="text-caption">
              Der Benutzer und alle zugehörigen Rollen werden unwiderruflich entfernt.
            </p>
            <div className="flex gap-3">
              <Button
                variant="destructive"
                size="sm"
                onClick={() => handleDelete(confirmDeleteId)}
                disabled={deletingId === confirmDeleteId}
              >
                {deletingId === confirmDeleteId ? "Wird gelöscht..." : "Endgültig löschen"}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setConfirmDeleteId(null)}>
                Abbrechen
              </Button>
            </div>
          </div>
        )}

        {/* User list */}
        <div className="divide-y divide-border border border-border">
          {loading ? (
            <div className="p-6 text-center text-caption">Wird geladen...</div>
          ) : users.length === 0 ? (
            <div className="p-6 text-center text-caption">Keine Operator-Benutzer gefunden</div>
          ) : (
            users.map((u) => (
              <div
                key={u.user_id}
                className="flex items-center justify-between p-4 hover:bg-secondary/20 transition-colors"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-medium shrink-0">
                    {(u.display_name || u.email || u.user_id).slice(0, 2).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-foreground truncate">
                      {u.display_name || u.email || u.user_id.slice(0, 8) + "…"}
                      {isSelf(u.user_id) && (
                        <span className="ml-2 text-xs text-muted-foreground">(Sie)</span>
                      )}
                    </p>
                    {u.email && (
                      <p className="text-xs text-muted-foreground truncate">{u.email}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {u.roles.map((role) => {
                    const config = roleLabels[role];
                    if (!config) return null;
                    const Icon = config.icon;
                    return (
                      <span
                        key={role}
                        className="flex items-center gap-1 text-xs px-2 py-1 border border-border bg-secondary/30 rounded-sm"
                      >
                        <Icon className={cn("w-3 h-3", config.color)} />
                        {config.label}
                      </span>
                    );
                  })}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => { startEdit(u); setShowCreate(false); setConfirmDeleteId(null); }}
                    className="ml-2"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                  {!isSelf(u.user_id) && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => { setConfirmDeleteId(u.user_id); setEditingUser(null); setShowCreate(false); }}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
